import React, { useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Star, ImagePlus, X, Loader2, MessageSquarePlus } from "lucide-react";

/**
 * props:
 *   interviewId   – string, optional (post-interview context)
 *   interviewRole – string, optional (job title for context)
 *   contextType   – "post_interview" | "dashboard"
 *   onDismiss     – () => void
 */
export default function FeedbackWidget({ interviewId, interviewRole, contextType = "dashboard", onDismiss }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [screenshotB64, setScreenshotB64] = useState("");
  const [screenshotPreview, setScreenshotPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Screenshot must be under 3 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setScreenshotB64(ev.target.result);
      setScreenshotPreview(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!rating) { toast.error("Please select a star rating"); return; }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        rating,
        comment: comment.trim(),
        screenshot_b64: screenshotB64,
        context_type: contextType,
        interview_id: interviewId || "",
        interview_role: interviewRole || "",
      });
      setSubmitted(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="border border-[#10B981]/30 bg-[#10B981]/5 p-6 flex items-center justify-between gap-4">
        <div>
          <div className="font-semibold text-[#10B981] text-sm mb-0.5">Thank you for your feedback!</div>
          <p className="text-xs text-muted-foreground">Your input helps us improve the platform for everyone.</p>
        </div>
        {onDismiss && (
          <button onClick={onDismiss} className="text-muted-foreground hover:text-foreground flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="border border-border bg-white p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <MessageSquarePlus className="w-4 h-4 text-[#0025D8]" />
          <div>
            <div className="font-semibold text-sm">How was your experience?</div>
            <p className="text-xs text-muted-foreground mt-0.5">Optional — takes 30 seconds</p>
          </div>
        </div>
        {onDismiss && (
          <button onClick={onDismiss} className="text-muted-foreground hover:text-foreground flex-shrink-0 mt-0.5">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Star rating */}
      <div className="flex items-center gap-1 mb-4">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => setRating(n)}
            className="p-0.5 transition-transform hover:scale-110"
            aria-label={`${n} star${n !== 1 ? "s" : ""}`}
          >
            <Star
              className="w-7 h-7 transition-colors"
              fill={(hover || rating) >= n ? "#F59E0B" : "none"}
              stroke={(hover || rating) >= n ? "#F59E0B" : "#d1d5db"}
            />
          </button>
        ))}
        {rating > 0 && (
          <span className="ml-2 text-xs text-muted-foreground font-mono">
            {["", "Poor", "Fair", "Good", "Great", "Excellent"][rating]}
          </span>
        )}
      </div>

      {/* Comment */}
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="What could be better? (optional)"
        rows={3}
        className="w-full border border-input bg-background px-3 py-2 text-sm rounded-none resize-none focus:outline-none focus:ring-2 focus:ring-[#0025D8]/30 focus:border-[#0025D8] mb-3"
      />

      {/* Screenshot upload */}
      <div className="mb-4">
        {screenshotPreview ? (
          <div className="relative inline-block">
            <img src={screenshotPreview} alt="Screenshot preview" className="h-20 w-auto border border-border object-cover" />
            <button
              type="button"
              onClick={() => { setScreenshotB64(""); setScreenshotPreview(""); if (fileRef.current) fileRef.current.value = ""; }}
              className="absolute -top-2 -right-2 bg-white border border-border rounded-full p-0.5 hover:bg-red-50 hover:border-red-300"
            >
              <X className="w-3 h-3 text-muted-foreground" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground hover:text-[#0025D8] border border-dashed border-border hover:border-[#0025D8]/40 px-3 py-2 transition-colors"
          >
            <ImagePlus className="w-3.5 h-3.5" />
            Attach screenshot (optional)
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || !rating}
          className="flex items-center gap-2 px-4 py-2 bg-[#0025D8] hover:bg-[#001CB3] disabled:opacity-40 text-white text-sm font-mono transition-colors"
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          {submitting ? "Submitting…" : "Submit feedback"}
        </button>
        {onDismiss && (
          <button type="button" onClick={onDismiss} className="text-xs text-muted-foreground hover:text-foreground font-mono">
            Skip
          </button>
        )}
      </div>
    </div>
  );
}
